import type { HelpGuide } from '../types';

export const ledgerGuide: HelpGuide = {
  key: 'ledger',
  title: 'บัญชีแยกประเภท (General Ledger)',
  subtitle: 'เมนู: บัญชีแยกประเภท ▸ บัญชีแยกประเภท',
  updated: 'ปรับปรุง 20 มิ.ย. 2569',
  docxUrl: '/help/ledger/คู่มือการใช้งาน_บัญชีแยกประเภท_DCC-ERP.docx',
  sections: [
    {
      id: 'overview',
      heading: '1. หน้านี้คืออะไร และใช้ทำอะไร',
      blocks: [
        {
          kind: 'para',
          text: 'หน้า “บัญชีแยกประเภท (General Ledger)” ใช้ดูรายการเคลื่อนไหวทั้งหมดของบัญชีหนึ่ง ๆ ในงวดที่เลือก โดยเลือกบัญชี (เฉพาะบัญชีระดับลงรายการได้/leaf) แล้วระบบจะแสดง ยอดยกมา · เดบิตที่เกิดในงวด · เครดิตที่เกิดในงวด · ยอดคงเหลือสะสม ทีละบรรทัด พร้อมยอดคงเหลือยกไป',
        },
        {
          kind: 'callout',
          text: 'หน้านี้เป็นหน้าดูอย่างเดียว (read-only) — แสดงเฉพาะรายการที่ลงบัญชีแล้ว (Posted) เท่านั้น ไม่มีการสร้าง/แก้ไข/ลบรายการ และไม่มีการลงบัญชีใด ๆ จากหน้านี้',
        },
        { kind: 'para', text: 'สิ่งที่ทำได้บนหน้านี้:' },
        {
          kind: 'bullets',
          items: [
            { lead: 'เลือกบัญชี + งวด — ', text: 'ดูรายการเคลื่อนไหวของบัญชีนั้นในเดือนที่เลือก' },
            {
              lead: 'กรองตามสาขา / หน่วยงาน — ',
              text: 'จำกัดให้แสดงเฉพาะรายการของสาขาหรือหน่วยงานที่ต้องการ',
            },
            {
              lead: 'ดูยอดคงเหลือสะสม — ',
              text: 'แต่ละบรรทัดแสดงยอดคงเหลือสะสม (running balance) ต่อเนื่องจากยอดยกมา',
            },
            {
              lead: 'ส่งออก Excel — ',
              text: 'ส่งออกรายการที่กำลังแสดงเป็นไฟล์ .xlsx ไปทำงานต่อ',
            },
          ],
        },
        {
          kind: 'image',
          src: '/help/ledger/01-ledger-overview.png',
          caption:
            'ภาพรวมหน้าบัญชีแยกประเภท — การ์ดสรุปยอดยกมา/เดบิต/เครดิต/คงเหลือ พร้อมตารางรายการเคลื่อนไหวและยอดคงเหลือสะสม',
        },
      ],
    },
    {
      id: 'anatomy',
      heading: '2. ส่วนประกอบของหน้าจอ',
      blocks: [
        { kind: 'para', text: 'หน้าจอแบ่งเป็น 4 ส่วนหลัก จากบนลงล่าง:' },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'ปุ่มด้านบน — ',
              text: 'ส่งออก (ดาวน์โหลดเป็นไฟล์ Excel) — ปุ่ม ส่งออก จะกดได้เมื่อมีข้อมูลในตารางแล้ว ส่วนปุ่ม พิมพ์ ที่อยู่ข้าง ๆ ยังไม่ได้เปิดใช้งาน (กดแล้วยังไม่ทำงาน)',
            },
            {
              lead: 'แถวตัวกรอง — ',
              text: 'เลือกบัญชี (leaf) · เลือกงวด (เดือน) · สาขา · หน่วยงาน และปุ่ม รีเซ็ต',
            },
            {
              lead: 'แถบสรุปยอด (การ์ด 5 ใบ) — ',
              text: 'ยอดยกมา · เดบิต (รวม) · เครดิต (รวม) · ยอดคงเหลือ · รายการ',
            },
            {
              lead: 'ตารางบัญชีแยกประเภท — ',
              text: 'รายการเคลื่อนไหวทีละบรรทัด พร้อมแถวรวมเคลื่อนไหวในงวดที่ท้ายตาราง',
            },
          ],
        },
      ],
    },
    {
      id: 'summary',
      heading: '3. แถบสรุปยอด (KPI)',
      blocks: [
        { kind: 'para', text: 'เมื่อเลือกบัญชีและงวดแล้ว ระบบจะแสดงการ์ดสรุป 5 ใบ:' },
        {
          kind: 'bullets',
          items: [
            { lead: 'ยอดยกมา — ', text: 'ยอดคงเหลือต้นงวด (ก่อนรายการในงวดนี้)' },
            { lead: 'เดบิต (รวม) — ', text: 'ผลรวมเดบิตที่เกิดขึ้นในงวด' },
            { lead: 'เครดิต (รวม) — ', text: 'ผลรวมเครดิตที่เกิดขึ้นในงวด' },
            { lead: 'ยอดคงเหลือ — ', text: 'ยอดคงเหลือยกไป (ปลายงวด) หลังรวมรายการในงวด' },
            { lead: 'รายการ — ', text: 'จำนวนบรรทัดรายการเคลื่อนไหวในงวด' },
          ],
        },
        {
          kind: 'para',
          tone: 'muted',
          text: 'ทิศทางของยอดคงเหลือเป็นไปตามสภาพบัญชี (Nature) — บัญชีสภาพเดบิต (สินทรัพย์/ต้นทุน/ค่าใช้จ่าย) เดบิตจะเพิ่มยอด ส่วนบัญชีสภาพเครดิต (หนี้สิน/ทุน/รายได้) เครดิตจะเพิ่มยอด',
        },
        {
          kind: 'image',
          src: '/help/ledger/02-summary-kpi.png',
          caption: 'แถบสรุปยอด — ยอดยกมา / เดบิตรวม / เครดิตรวม / ยอดคงเหลือ / จำนวนรายการ',
        },
      ],
    },
    {
      id: 'filters',
      heading: '4. แถวตัวกรอง — เลือกบัญชี / งวด',
      blocks: [
        {
          kind: 'para',
          text: 'เริ่มจากเลือกบัญชีที่ช่อง “— เลือกบัญชี (leaf) —” (เลือกได้เฉพาะบัญชีระดับลงรายการได้ ไม่ใช่บัญชีคุมยอด) จากนั้นเลือกงวด (เดือน) ที่ต้องการดู ระบบจะดึงรายการของบัญชีนั้นในงวดที่เลือกมาแสดงทันที',
        },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'เลือกบัญชี (leaf) — ',
              text: 'จำเป็นต้องเลือก ระบบจึงจะแสดงรายการ (ก่อนเลือกจะขึ้นข้อความ “เลือกบัญชีจากรายการด้านบนเพื่อแสดงรายการ”)',
            },
            {
              lead: 'งวด (เดือน) — ',
              text: 'ค่าเริ่มต้นเป็นเดือนปัจจุบัน เปลี่ยนเป็นเดือนอื่นเพื่อย้อนดูได้',
            },
            {
              lead: 'รีเซ็ต — ',
              text: 'ล้างตัวกรอง — งวดกลับเป็นเดือนปัจจุบัน และสาขา/หน่วยงานกลับเป็น “ทั้งหมด”',
            },
          ],
        },
        {
          kind: 'callout',
          variant: 'info',
          text: 'เมื่อโหลดข้อมูลแล้ว จะมีข้อความกำกับชนิดและสภาพของบัญชี (เช่น ASSET · เดบิต-nature) แสดงข้าง ๆ ช่องตัวกรอง',
        },
        {
          kind: 'image',
          src: '/help/ledger/03-filters-account-selector.png',
          caption: 'แถวตัวกรอง — เลือกบัญชี (leaf) / งวด / สาขา / หน่วยงาน',
        },
      ],
    },
    {
      id: 'filter-branch',
      heading: '5. กรองตามสาขา',
      blocks: [
        {
          kind: 'para',
          text: 'ช่อง “สาขา: ทั้งหมด” ใช้จำกัดให้แสดงเฉพาะรายการที่อยู่ในสาขาที่เลือก เมื่อเลือกสาขาใดสาขาหนึ่ง ตารางและยอดสรุปจะปรับให้แสดงเฉพาะรายการของสาขานั้น เหมาะกับการกระทบยอดหรือตรวจรายการของแต่ละสาขา',
        },
        {
          kind: 'image',
          src: '/help/ledger/04-filter-by-branch.png',
          caption: 'กรองตามสาขา — แสดงเฉพาะรายการเคลื่อนไหวของสาขาที่เลือก',
        },
      ],
    },
    {
      id: 'filter-department',
      heading: '6. กรองตามหน่วยงาน',
      blocks: [
        {
          kind: 'para',
          text: 'ช่อง “หน่วยงาน: ทั้งหมด” ใช้จำกัดให้แสดงเฉพาะรายการที่ระบุหน่วยงาน (ศูนย์ต้นทุน) ที่เลือก เมื่อเลือกหน่วยงาน ตารางและยอดสรุปจะปรับตามรายการของหน่วยงานนั้น',
        },
        {
          kind: 'image',
          src: '/help/ledger/05-filter-by-department.png',
          caption: 'กรองตามหน่วยงาน — แสดงเฉพาะรายการเคลื่อนไหวของหน่วยงานที่เลือก',
        },
      ],
    },
    {
      id: 'multi-filter',
      heading: '7. กรองหลายเงื่อนไขพร้อมกัน',
      blocks: [
        {
          kind: 'para',
          text: 'สามารถใช้ตัวกรอง บัญชี + สาขา + หน่วยงาน ร่วมกันได้พร้อมกัน ระบบจะแสดงเฉพาะรายการที่ตรงตามทุกเงื่อนไข พร้อมยอดสรุป (ยอดยกมา/เดบิต/เครดิต/คงเหลือ) ที่คำนวณจากรายการที่กรองแล้ว เหมาะกับการเจาะดูรายการเฉพาะเจาะจง',
        },
        {
          kind: 'image',
          src: '/help/ledger/06-multi-filter.png',
          caption: 'กรองหลายเงื่อนไขพร้อมกัน — บัญชี + สาขา + หน่วยงาน',
        },
      ],
    },
    {
      id: 'table',
      heading: '8. ตารางบัญชีแยกประเภท',
      blocks: [
        { kind: 'para', text: 'ตารางแสดงรายการเคลื่อนไหวทีละบรรทัด คอลัมน์ (ซ้ายไปขวา):' },
        {
          kind: 'table',
          head: ['คอลัมน์', 'ความหมาย'],
          rows: [
            ['วันที่', 'วันที่ของใบสำคัญ'],
            ['เลขที่ใบสำคัญ', 'เลขที่ใบสำคัญที่อ้างถึงรายการนี้'],
            ['คำอธิบาย', 'คำอธิบายของบรรทัด (หรือคำอธิบายใบสำคัญถ้าไม่ระบุเฉพาะบรรทัด)'],
            ['สาขา', 'ชื่อสาขาของรายการ (“—” ถ้าไม่ระบุ)'],
            ['หน่วยงาน', 'ชื่อหน่วยงานของรายการ (“—” ถ้าไม่ระบุ)'],
            ['อ้างอิง', 'รหัสอ้างอิงข้ามเอกสาร (เว้นว่างถ้าไม่มี)'],
            ['เดบิต', 'จำนวนเงินเดบิต (“—” ถ้าเป็น 0)'],
            ['เครดิต', 'จำนวนเงินเครดิต (“—” ถ้าเป็น 0)'],
            ['ยอดคงเหลือ', 'ยอดคงเหลือสะสมหลังรวมบรรทัดนี้'],
          ],
        },
        {
          kind: 'para',
          text: 'ท้ายตารางมีแถว “รวมเคลื่อนไหวในงวด” แสดงผลรวมเดบิต ผลรวมเครดิต และยอดคงเหลือยกไปของงวด',
        },
      ],
    },
    {
      id: 'export',
      heading: '9. ส่งออก Excel',
      blocks: [
        {
          kind: 'para',
          text: 'เมื่อมีรายการในตารางแล้ว สามารถกดปุ่ม ส่งออก เพื่อดาวน์โหลดเป็นไฟล์ Excel (.xlsx) สำหรับนำไปทำงานต่อ ชื่อไฟล์จะอ้างอิงบริษัท บัญชี และงวดที่เลือก',
        },
        {
          kind: 'callout',
          variant: 'info',
          text: 'ไฟล์ที่ส่งออกจะมีเฉพาะรายการที่กำลังแสดงอยู่บนหน้าจอ (ตามตัวกรองบัญชี/งวด/สาขา/หน่วยงานที่ตั้งไว้)',
        },
        {
          kind: 'callout',
          text: 'หมายเหตุ: ปุ่ม พิมพ์ ที่อยู่ข้างปุ่ม ส่งออก ยังไม่ได้เปิดใช้งานในขณะนี้ — หากต้องการรายงานให้ใช้ปุ่ม ส่งออก แล้วสั่งพิมพ์จากไฟล์ Excel แทน',
        },
        {
          kind: 'image',
          src: '/help/ledger/07-export-action.png',
          caption: 'ปุ่ม ส่งออก — ดาวน์โหลดรายการที่กำลังแสดงเป็นไฟล์ Excel',
        },
      ],
    },
    {
      id: 'faq',
      heading: '10. คำถามที่พบบ่อยและข้อควรระวัง',
      blocks: [
        { kind: 'para', text: 'ทำไมเลือกบัญชีในรายการไม่ครบทุกตัว?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'หน้านี้แสดงเฉพาะบัญชีระดับลงรายการได้ (leaf) เท่านั้น บัญชีคุมยอด/บัญชีหัวข้อรวมจะไม่อยู่ในรายการให้เลือก',
            },
          ],
        },
        { kind: 'para', text: 'เลือกบัญชีแล้วแต่ขึ้นว่า “ไม่มีรายการในงวดนี้”?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'แปลว่าบัญชีนั้นไม่มีรายการเคลื่อนไหวในงวดที่เลือก ลองเปลี่ยนงวด หรือล้างตัวกรองสาขา/หน่วยงานที่อาจแคบเกินไป',
            },
          ],
        },
        { kind: 'para', text: 'ปุ่ม ส่งออก กดไม่ได้ / ปุ่ม พิมพ์ กดแล้วไม่ทำงาน?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ปุ่ม ส่งออก จะกดได้เมื่อมีรายการแสดงในตารางแล้วเท่านั้น — ถ้ายังไม่มีข้อมูลให้เลือกบัญชี/งวดก่อน',
            },
            {
              text: 'ปุ่ม พิมพ์ ยังไม่ได้เปิดใช้งานในขณะนี้ จึงกดแล้วยังไม่ทำงาน — ให้ใช้ปุ่ม ส่งออก แล้วสั่งพิมพ์จากไฟล์ Excel แทน',
            },
          ],
        },
        { kind: 'para', text: 'แก้ไขหรือลบรายการจากหน้านี้ได้ไหม?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ไม่ได้ — หน้านี้เป็นหน้าดูอย่างเดียว หากต้องการแก้ไขรายการต้องไปที่เอกสารต้นทาง (เช่น ใบสำคัญที่ /journal)',
            },
          ],
        },
      ],
    },
    {
      id: 'glossary',
      heading: '11. อภิธานศัพท์',
      blocks: [
        {
          kind: 'bullets',
          items: [
            {
              lead: 'บัญชีแยกประเภท (General Ledger) — ',
              text: 'รายการเคลื่อนไหวทั้งหมดของบัญชีหนึ่ง ๆ เรียงตามลำดับ พร้อมยอดคงเหลือสะสม',
            },
            {
              lead: 'บัญชี leaf (ระดับลงรายการได้) — ',
              text: 'บัญชีปลายทางที่ลงรายการได้จริง (ไม่ใช่บัญชีคุมยอด/หัวข้อรวม)',
            },
            { lead: 'ยอดยกมา — ', text: 'ยอดคงเหลือต้นงวด ก่อนรวมรายการที่เกิดในงวด' },
            {
              lead: 'ยอดคงเหลือสะสม (Running Balance) — ',
              text: 'ยอดคงเหลือสะสมต่อเนื่องทีละบรรทัด นับจากยอดยกมา',
            },
            {
              lead: 'สภาพบัญชี (Nature) — ',
              text: 'เดบิต หรือ เครดิต — กำหนดว่าด้านใดทำให้ยอดคงเหลือเพิ่มขึ้น',
            },
            {
              lead: 'สาขา / หน่วยงาน — ',
              text: 'มิติของรายการ (สาขา = สถานที่, หน่วยงาน = ศูนย์ต้นทุน) ใช้กรองรายการ',
            },
            {
              lead: 'อ้างอิง (Ref) — ',
              text: 'รหัสอ้างอิงข้ามเอกสาร เช่น เลขที่บิลเจ้าหนี้/ใบแจ้งหนี้ลูกหนี้',
            },
          ],
        },
      ],
    },
  ],
};
